import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import Swal from 'sweetalert2';
import apiClient, { get, post, patch } from '../../services/api/apiClient.js';
import { AuthContext } from '../../contexts/AuthContext.jsx';
import { Pencil } from 'lucide-react';
import DataTable from '../../components/common/DataTable.jsx';
import Pagination from '../../components/common/Pagination.jsx';
import Modal from '../../components/common/Modal.jsx';
import { formatMoney, formatDate, today, daysAgo, primero, useLocalTable } from '../finanzas-cxp/shared.js';
import { exportTablePdf, buildTablePdf, exportTableExcel } from '../../utils/exportUtils.js';
import EmailModal from '../../components/common/EmailModal.jsx';
import { enviarCorreoAdjunto, plantillaCorreoV1 } from '../../services/api/mail.js';
import { puedeEditarCargo } from '../../config/resources.finanzas-cxc.jsx';

/**
 * Cargos — página BESPOKE (no `ResourcePage` genérico + `FormModal`).
 *
 * TAREA 2 de la segunda ronda de la auditoría CxC 2026-09-28 (ver DUDAS.md, gap #2
 * "Calculadora ligada %/$ de comisión y USD/tipoCambio/total"): el `ResourcePage`
 * genérico (usado antes aquí vía `RESOURCES_FINANZAS_CXC.cargos`, ver
 * `src/config/resources.finanzas-cxc.jsx`) no soporta campos derivados en vivo —
 * solo inputs `type:'number'` sueltos e independientes.
 *
 * V1 (`IbuFront/src/components/views/Admin/Cargos/CargosCreate.jsx` líneas ~71-81 y
 * el bloque condicionado por `moneda` líneas ~253-366, réplica también en
 * `ListadoCargos.jsx` para el modal de edición) tiene DOS calculadoras ligadas:
 *   - USD/tipoCambio/total (igual patrón que `CargosContenedorPage.jsx`, que ya
 *     resolvió este mismo gap para Cargos a Contenedor en la misma auditoría):
 *     moneda USD -> `total = totalUSD * tipoCambio` (editar cualquiera de los 2
 *     recalcula `total`, que queda de solo lectura); moneda MXN -> se captura
 *     `total` directo y se deriva `totalUSD = total / tipoCambio`.
 *   - Comisión %/$ (que CargosContenedor NO tiene, Cargos sí): editar
 *     `comisionPorcentaje` recalcula `comisionNumero = total * (pct/100)`; editar
 *     `comisionNumero` recalcula `comisionPorcentaje = (numero/total)*100`. Ambos
 *     también se recalculan cada vez que cambia `total` (por la calculadora de
 *     USD/MXN) — igual que V1, que siempre deriva `comisionNumero` a partir del
 *     `total` vigente en `calcula`/`calculaTipoCambio`/`onChange` de Total MXN.
 * Con 2 calculadoras interdependientes (5 campos numéricos ligados) + visibilidad
 * condicional de campos por `moneda`, el gap es demasiado grande para el `FormModal`
 * genérico (usado por muchos otros recursos: catálogos simples, Abonos,
 * Devoluciones, etc.) sin arriesgar romperlos — se eligió Opción B (bespoke), mismo
 * patrón ya usado por `CargosContenedorPage.jsx`/`GastosContenedorPage.jsx`.
 *
 * TAREA 1 de la misma auditoría (gap #1, guard "no editar Cargo ya abonado"): el
 * botón "Editar" solo se muestra si `puedeEditarCargo(row)` (ver
 * `resources.finanzas-cxc.jsx` — réplica exacta de `ListadoCargos.jsx` L700:
 * `saldo == total + comisionNumero`, es decir, mientras el Cargo no tenga ningún
 * abono aplicado). El backend (`cargos.service.js#editarImporte`) valida lo mismo
 * server-side (un guard solo de frontend es evitable con una llamada directa).
 *
 * Reglas de negocio del backend (`cargos.service.js`, confirmadas contra el
 * validator y la config auditada `resources.finanzas-cxc.jsx`):
 *   - Alta: `POST /cargos` con `abonos:0, saldo:total+comisionNumero` (V1
 *     `CargosCreate.jsx#saveCargo`: `saldo:parseFloat(total)+parseFloat(comisionNumero)`).
 *   - Edición: DEBE ir a `PATCH /cargos/:id/importe` (`editarImporte()`), NUNCA al
 *     PATCH genérico (`editar()`, que no reajusta `Clientes.cargos/saldo` — bug real
 *     ya documentado en `resources.finanzas-cxc.jsx`).
 *   - "Pagar": `PATCH /cargos/:id/pagar {saldo: monto}` (nombre de campo heredado de
 *     V1 — es el MONTO ABONADO, no el saldo resultante).
 *   - Baja: `DELETE /cargos/:id` (baja lógica -> `cancelar()`).
 */
const RECURSO = 'cargos';
const ROUTE = 'cargos';

function valoresIniciales(row) {
  if (!row) {
    return {
      fecha: today(), clientes: '', conceptosGastos: '', descripcion: 'NA',
      moneda: 'MXN', totalUSD: 0, tipoCambio: 1, total: 0, comisionPorcentaje: 0, comisionNumero: 0,
    };
  }
  return {
    fecha: row.fecha, clientes: row._idCliente, conceptosGastos: row._idConcepto,
    descripcion: row.descripcion, moneda: row.moneda || 'MXN',
    totalUSD: row.totalUSD, tipoCambio: row.tipoCambio, total: row.total,
    comisionPorcentaje: row.comisionPorcentaje || 0, comisionNumero: row.comisionNumero || 0,
  };
}

function CargoForm({ open, onClose, title, initial, conceptos, clientes, onSubmit, esEdicion }) {
  const [v, setV] = useState(valoresIniciales(initial));
  const [guardando, setGuardando] = useState(false);

  useEffect(() => { if (open) setV(valoresIniciales(initial)); }, [open, initial]);

  function set(k, val) { setV((s) => ({ ...s, [k]: val })); }

  function cambiaMoneda(m) { setV((s) => ({ ...s, moneda: m, totalUSD: 0, tipoCambio: 1, total: 0, comisionPorcentaje: 0, comisionNumero: 0 })); }

  // ---- Calculadora ligada USD <-> tipoCambio <-> total (mismo patrón que CargosContenedorPage.jsx) ----
  // Cada una además recalcula `comisionNumero` a partir del `total` resultante y el
  // `comisionPorcentaje` vigente -- igual que V1 (`calcula`/`calculaTipoCambio`/
  // `onChange` de Total MXN en `CargosCreate.jsx`/`ListadoCargos.jsx`).
  function cambiaTotalUSD(val) {
    const totalUSD = Number(val) || 0;
    setV((s) => {
      const total = totalUSD * (Number(s.tipoCambio) || 0);
      return { ...s, totalUSD, total, comisionNumero: total * ((Number(s.comisionPorcentaje) || 0) / 100) };
    });
  }
  function cambiaTipoCambio(val) {
    const tipoCambio = Number(val) || 0;
    setV((s) => {
      const total = (Number(s.totalUSD) || 0) * tipoCambio;
      return { ...s, tipoCambio, total, comisionNumero: total * ((Number(s.comisionPorcentaje) || 0) / 100) };
    });
  }
  function cambiaTotalMXN(val) {
    const total = Number(val) || 0;
    setV((s) => ({
      ...s, total,
      totalUSD: s.tipoCambio ? total / s.tipoCambio : 0,
      comisionNumero: total * ((Number(s.comisionPorcentaje) || 0) / 100),
    }));
  }

  // ---- Calculadora ligada Comisión % <-> $ (propia de Cargos, CargosContenedor no la tiene) ----
  function cambiaComisionPorcentaje(val) {
    const pct = Number(val) || 0;
    setV((s) => ({ ...s, comisionPorcentaje: pct, comisionNumero: (Number(s.total) || 0) * (pct / 100) }));
  }
  function cambiaComisionNumero(val) {
    const numero = Number(val) || 0;
    setV((s) => ({ ...s, comisionNumero: numero, comisionPorcentaje: s.total ? Number(((numero / s.total) * 100).toFixed(2)) : 0 }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    // V1 (`CargosCreate.jsx#saveCargo`): cliente, concepto y total != 0 obligatorios.
    if (!v.clientes || !v.conceptosGastos || !(Number(v.total) > 0) || !v.fecha) {
      Swal.fire({ icon: 'error', title: 'Oops...', text: 'Favor de completar todos los campos' });
      return;
    }
    setGuardando(true);
    try {
      await onSubmit(v);
      Swal.fire('Good job!', esEdicion ? 'Actualizado con exito' : 'Creado con exito', 'success');
      onClose();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'No se pudo guardar', text: err.message });
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={title} width="max-w-2xl"
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn-outline">Cancelar</button>
          <button type="submit" form="cargo-form" disabled={guardando} className="btn-primary">
            {guardando ? 'Guardando...' : 'Guardar'}
          </button>
        </>
      )}
    >
      <form id="cargo-form" onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="label">Fecha</label>
          <input type="date" required value={v.fecha} onChange={(e) => set('fecha', e.target.value)}
            readOnly={esEdicion} className="input-base" />
        </div>
        <div>
          <label className="label">Cliente</label>
          <select required value={v.clientes} onChange={(e) => set('clientes', e.target.value)}
            className="select-base">
            <option value="">Selecciona</option>
            {[...clientes].sort((a, b) => (a.nombre_comercial > b.nombre_comercial ? 1 : -1)).map((c) => (
              <option key={c._id} value={c._id}>{c.nombre_comercial}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Concepto</label>
          <select required value={v.conceptosGastos} onChange={(e) => set('conceptosGastos', e.target.value)}
            className="select-base">
            <option value="">Selecciona</option>
            {[...conceptos].sort((a, b) => (a.name > b.name ? 1 : -1)).map((c) => (
              <option key={c._id} value={c._id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Descripcion</label>
          <input type="text" required value={v.descripcion} onChange={(e) => set('descripcion', e.target.value)}
            className="input-base" />
        </div>
        <div>
          <label className="label">Moneda</label>
          <select value={v.moneda} onChange={(e) => cambiaMoneda(e.target.value)}
            className="select-base">
            <option value="MXN">MXN</option>
            <option value="USD">USD</option>
          </select>
        </div>
        <div />
        {v.moneda === 'USD' ? (
          <>
            <div>
              <label className="label">Total USD</label>
              <input type="number" min="0" step="any" required value={v.totalUSD} onChange={(e) => cambiaTotalUSD(e.target.value)}
                className="input-base" />
            </div>
            <div>
              <label className="label">Tipo de Cambio</label>
              <input type="number" min="0" step="any" required value={v.tipoCambio} onChange={(e) => cambiaTipoCambio(e.target.value)}
                className="input-base" />
            </div>
            <div>
              <label className="label">Total MXN</label>
              <input type="number" disabled value={v.total.toFixed(2)} className="input-base" />
            </div>
            <div />
          </>
        ) : (
          <div>
            <label className="label">Total MXN</label>
            <input type="number" min="0" step="any" required value={v.total} onChange={(e) => cambiaTotalMXN(e.target.value)}
              className="input-base" />
          </div>
        )}
        <div>
          <label className="label">Comisión %</label>
          <input type="number" min="0" step="any" value={v.comisionPorcentaje} onChange={(e) => cambiaComisionPorcentaje(e.target.value)}
            className="input-base" />
        </div>
        <div>
          <label className="label">Comisión $</label>
          <input type="number" min="0" step="any" value={v.comisionNumero} onChange={(e) => cambiaComisionNumero(e.target.value)}
            className="input-base" />
        </div>
      </form>
    </Modal>
  );
}

export default function CargosPage() {
  const { hasPermiso } = useContext(AuthContext);
  const puedeVer = hasPermiso(`${RECURSO}:ver`);
  const puedeCrear = hasPermiso(`${RECURSO}:crear`);
  const puedeEditar = hasPermiso(`${RECURSO}:editar`);
  const puedeEliminar = hasPermiso(`${RECURSO}:eliminar`);

  const [cargos, setCargos] = useState([]);
  const [conceptos, setConceptos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [mailOpen, setMailOpen] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const [cargosData, concData, cliData] = await Promise.all([
        get(`/${ROUTE}`),
        apiClient.get('/conceptos-gastos', { params: { limit: 500 } }).then((r) => r.data.data),
        apiClient.get('/clientes', { params: { limit: 500 } }).then((r) => r.data.data),
      ]);
      setConceptos(concData || []);
      setClientes(cliData || []);
      setCargos((cargosData || []).map((c) => {
        const conc = primero(c.conceptosGastos);
        const cli = primero(c.clientes);
        return {
          _id: c._id,
          numero: c.idCargo,
          fecha: c.fecha,
          descripcion: c.descripcion,
          concepto: conc?.name || '—',
          _idConcepto: conc?._id,
          cliente: cli?.nombre_comercial || '—',
          _idCliente: cli?._id,
          totalUSD: c.totalUSD,
          tipoCambio: c.tipoCambio,
          total: c.total,
          abonos: c.abonos,
          saldo: c.saldo,
          moneda: c.moneda,
          comisionPorcentaje: c.comisionPorcentaje,
          comisionNumero: c.comisionNumero,
          is_active: c.is_active,
        };
      }));
    } catch (e) {
      setError(e.message);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // Filtros de Fecha Inicio/Fin y Cliente (segunda ronda de auditoría CxC,
  // 2026-09-28 -- DUDAS.md "Gaps... NO implementados #3"): V1 (`ListadoCargos.jsx`)
  // siempre traía estos 2 filtros además del buscador de texto libre; esta página se
  // volvió bespoke (Tarea 2, calculadora) así que se agregan aquí en vez de en
  // `ResourcePage#filtrosExtra` (que sí se usa en los recursos de `ResourcePage`
  // genérico -- Devoluciones/DevolucionesInversa -- ver `resources.finanzas-cxc.jsx`).
  // 100% en cliente (igual que el resto de esta página, `/cargos` ya trae TODOS los
  // activos sin paginar), aplicado ANTES de `useLocalTable` para que búsqueda + orden
  // + export PDF/Excel respeten el filtro (mismo criterio que V1).
  const [selectedFechaInicio, setSelectedFechaInicio] = useState(daysAgo(30));
  const [selectedFechaFin, setSelectedFechaFin] = useState(today());
  const [selectedCliente, setSelectedCliente] = useState('');

  const cargosFiltrados = useMemo(
    () => cargos.filter((c) =>
      (!selectedFechaInicio || c.fecha >= selectedFechaInicio) &&
      (!selectedFechaFin || c.fecha <= selectedFechaFin) &&
      (!selectedCliente || c._idCliente === selectedCliente)
    ),
    [cargos, selectedFechaInicio, selectedFechaFin, selectedCliente]
  );

  const { search, setSearch, page, setPage, sortBy, sortOrder, ordenarPor, pageRows, filtradas, pagination } = useLocalTable(cargosFiltrados, {
    searchFields: ['concepto', 'cliente', 'descripcion'], perPage: 50,
    defaultSortBy: 'fecha', defaultSortOrder: 'desc',
  });

  // PDF/Excel de V1 (`ListadoCargos.jsx`): Fecha, Concepto, Descripcion, Importe (+ Total).
  const columnasExport = [
    { key: 'fecha', label: 'Fecha' },
    { key: 'concepto', label: 'Concepto' },
    { key: 'descripcion', label: 'Descripcion' },
    { key: 'total', label: 'Importe', render: (r) => formatMoney(r.total) },
  ];
  const totalFiltrado = filtradas.reduce((t, r) => t + (Number(r.total) || 0), 0);

  const opcionesPdf = { fileName: 'DetalleCargos', totalCols: { descripcion: 'Total', total: formatMoney(totalFiltrado) } };

  async function guardar(valores) {
    const payload = {
      fecha: valores.fecha,
      totalUSD: Number(valores.totalUSD),
      tipoCambio: Number(valores.tipoCambio),
      total: Number(valores.total),
      descripcion: valores.descripcion,
      conceptosGastos: valores.conceptosGastos,
      clientes: valores.clientes,
      moneda: valores.moneda,
      comisionPorcentaje: Number(valores.comisionPorcentaje) || 0,
      comisionNumero: Number(valores.comisionNumero) || 0,
    };
    if (editando) {
      // Igual que V1 (`ListadoCargos.jsx#editCargo`): la edición solo es posible
      // cuando el Cargo no tiene abonos aplicados (guard de arriba), así que
      // `saldo = total + comisionNumero` siempre coincide con "recién creado".
      await patch(`/${ROUTE}/${editando._id}/importe`, { ...payload, saldo: payload.total + payload.comisionNumero });
    } else {
      await post(`/${ROUTE}`, { ...payload, abonos: 0, saldo: payload.total + payload.comisionNumero });
    }
    await cargar();
  }

  const columnas = [
    { key: 'numero', label: 'Numero', sortable: true },
    { key: 'fecha', label: 'Fecha', sortable: true, render: (r) => formatDate(r.fecha) },
    { key: 'concepto', label: 'Concepto', sortable: true },
    { key: 'descripcion', label: 'Descripción', sortable: true },
    { key: 'cliente', label: 'Cliente', sortable: true },
    { key: 'total', label: 'Importe', sortable: true, className: 'text-right', render: (r) => formatMoney(r.total) },
    { key: 'comisionNumero', label: 'Comision', sortable: true, className: 'text-right', render: (r) => formatMoney(r.comisionNumero) },
    {
      key: '__acciones', label: 'Editar', sortable: false, className: 'text-center',
      render: (row) => (
        <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
          {/* Guard "no editar si ya tiene abonos" (segunda ronda de auditoría CxC
              2026-09-28, ver DUDAS.md) -- réplica exacta de `ListadoCargos.jsx`
              L700 (`c.saldo == c.total + c.comision`). */}
          {puedeEditar && row.is_active !== 'No' && puedeEditarCargo(row) && (
            <button title="Editar" aria-label="Editar" className="p-1.5 rounded-md text-primary-600 hover:bg-primary-50" onClick={() => { setEditando(row); setFormOpen(true); }}>
              <Pencil size={15} />
            </button>
          )}
        </div>
      ),
    },
  ];

  if (!puedeVer) return <div className="text-slate-500">No tienes permiso para ver este módulo.</div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-slate-900">Cargos</h1>
        {puedeCrear && (
          <button onClick={() => { setEditando(null); setFormOpen(true); }} className="btn-primary">
            + Nuevo Cargo
          </button>
        )}
      </div>

      <div className="flex items-center gap-3">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar..."
          className="input-base w-64" />
        <button type="button" onClick={() => exportTablePdf('Detalle de Cargos', columnasExport, filtradas, opcionesPdf)}
          className="btn-outline text-xs px-3 py-1.5">PDF</button>
        <button type="button" onClick={() => setMailOpen(true)}
          className="btn-outline text-xs px-3 py-1.5">eMail</button>
        <button type="button" onClick={() => exportTableExcel('Detalle de Cargos', columnasExport, filtradas, { fileName: `DetalleCargos-${selectedFechaInicio}-${selectedFechaFin}`, sheetName: 'DetalleCargos' })}
          className="btn-outline text-xs px-3 py-1.5">Excel</button>
        {error && <span className="text-sm text-danger-600">{error}</span>}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="label">Fecha Inicio</label>
          <input type="date" value={selectedFechaInicio} onChange={(e) => { setSelectedFechaInicio(e.target.value); setPage(1); }}
            className="input-base" />
        </div>
        <div>
          <label className="label">Fecha Fin</label>
          <input type="date" value={selectedFechaFin} onChange={(e) => { setSelectedFechaFin(e.target.value); setPage(1); }}
            className="input-base" />
        </div>
        <div>
          <label className="label">Cliente</label>
          <select value={selectedCliente} onChange={(e) => { setSelectedCliente(e.target.value); setPage(1); }}
            className="select-base">
            <option value="">Selecciona</option>
            {[...clientes].sort((a, b) => (a.nombre_comercial > b.nombre_comercial ? 1 : -1)).map((c) => (
              <option key={c._id} value={c._id}>{c.nombre_comercial}</option>
            ))}
          </select>
        </div>
      </div>

      <DataTable columns={columnas} data={pageRows} loading={cargando} sortBy={sortBy} sortOrder={sortOrder} onSort={ordenarPor}
        footer={{ cliente: 'TOTAL', total: formatMoney(pageRows.reduce((t, r) => t + (Number(r.total) || 0), 0)) }} />
      <Pagination pagination={pagination} page={page} onChange={setPage} />

      <EmailModal
        open={mailOpen} onClose={() => setMailOpen(false)} titulo="Enviar Listado Cargos"
        onEnviar={async (email) => {
          const doc = await buildTablePdf('Detalle de Cargos', columnasExport, filtradas, opcionesPdf);
          await enviarCorreoAdjunto({
            subject: 'Lista de Cargos', email, fileName: 'ListaCargos.pdf', docPdf: doc,
            textoHtml: plantillaCorreoV1('Adjunto encontraras la lista de Cargos.'),
          });
        }}
      />

      <CargoForm
        open={formOpen} onClose={() => setFormOpen(false)}
        title={editando ? `Editar Cargo ${editando.numero}` : 'Nuevo Cargo'}
        initial={editando} conceptos={conceptos} clientes={clientes}
        onSubmit={guardar} esEdicion={!!editando}
      />
    </div>
  );
}
